import { Log } from '@lightningjs/sdk';
import { ChannelModel } from './EpgApi.types';
import { Environment } from '../config/environment';
import { Backend } from './Backend';
import { IChannelRightsV5, IChannelV5 } from './models/AndromeTV.types';
import { StreamType } from './models/Shared.types';
import { EpgApiV5 } from './EpgApiV5';

export class ChannelManager {
  static channels(backend: Backend): Promise<ChannelModel[]> {
    const channelsProm = this._getChannels(backend);
    const rightsProm = this._getRights(backend);

    return Promise.all([channelsProm, rightsProm]).then(([channels, rights]) => {
      return channels.map(channel => {
        const ch = new ChannelModel(channel);
        rights.some(right => {
          if (right.channelId === ch.channelId) {
            ch.rights = right;
            return true;
          }
          return false;
        });
        return ch;
      });
    });
  }

  static channel(backend: Backend, id: string) {
    return this.channels(backend).then(channels => {
      return channels.find(channel => channel.channelId === id);
    });
  }

  static validChannels(
    backend: Backend,
    replay: boolean
  ): Promise<{
    allChannels: ChannelModel[];
    validChannels: ChannelModel[];
  }> {
    const requiredOttRights = replay
      ? (Environment.atv_replay_ott_rights as StreamType[])
      : (Environment.atv_ott_rights as StreamType[]);
    const catalogId = backend.deviceProfile?.profile.catalog;
    const prom = this.channels(backend);

    return prom.then(channelModels => {
      const channels = channelModels.filter(
        channel => !channel.catalogs || channel.catalogs.some(catalog => catalog.catalogId === catalogId)
      );

      const validChannels = channels.filter(channel => {
        if (channel.adult) return false;
        if (!channel.enabled || channel.audioOnly) {
          return false;
        }

        if (backend.isAuthorized()) {
          return channel.hasOneofMaxOttRights(requiredOttRights);
        }

        return channel.hasOneOfCustomerRights(requiredOttRights);
      });

      return {
        allChannels: channels,
        validChannels,
      };
    });
  }

  private static _getChannels(backend: Backend): Promise<IChannelV5[]> {
    return EpgApiV5.channels(backend).catch(error => {
      Log.error(error);
      return [];
    });
  }

  private static _getRights(backend: Backend): Promise<IChannelRightsV5[]> {
    return EpgApiV5.channelRights(backend).catch(error => {
      Log.error(error);
      return [];
    });
  }
}
